import { Cloud, Wind, Droplets } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getCurrentWeather, WeatherResponse } from '../../services/weatherApi';

export default function WeatherWidget() {
  const [weather, setWeather] = useState<WeatherResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getCurrentWeather();
        setWeather(data);
      } catch (err) {
        setError('Kunne ikke laste værdata. Vennligst prøv igjen senere.');
        console.error('Failed to fetch weather:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    
    // Refresh weather data every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center gap-2 mb-6">
          <Cloud className="w-6 h-6 text-[#2173B9]" />
          <h3 className="text-lg font-semibold">Værforhold</h3>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-6 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center gap-2 mb-6">
          <Cloud className="w-6 h-6 text-[#2173B9]" />
          <h3 className="text-lg font-semibold">Værforhold</h3>
        </div>
        <div className="text-gray-600">
          <p>Værdata er midlertidig utilgjengelig.</p>
          <p className="text-sm mt-2">Vennligst prøv igjen senere.</p>
        </div>
      </div>
    );
  }

  const windSpeedMS = (weather.current.wind_kph / 3.6).toFixed(1);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Cloud className="w-6 h-6 text-[#2173B9]" />
        <h3 className="text-lg font-semibold">Værforhold</h3>
      </div>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="text-4xl font-bold">{weather.current.temp_c}°C</span>
            <img
              src={`https:${weather.current.condition.icon}`}
              alt={weather.current.condition.text}
              className="w-12 h-12"
              loading="lazy"
            />
          </div>
          <span className="text-gray-600">{weather.current.condition.text}</span>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Wind className="w-4 h-4" />
            <span>{windSpeedMS} m/s</span>
          </div>
          <div className="flex items-center gap-1">
            <Droplets className="w-4 h-4" />
            <span>{weather.current.humidity}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
